import { notify, confirm } from "../../utils/notify";
import React, { useState, useEffect } from "react";
import Header from "../Header";
import {
  complaintsAPI,
  uploadAttachment,
  openExpenseAttachment,
} from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import {
  useListControls,
  ListToolbar,
  Pagination,
} from "../../components/ListControls";
import Topbar from "../../components/Topbar";

const CATEGORIES = [
  "Harassment / Misconduct",
  "Workplace Safety",
  "Payroll / Compensation",
  "HR Policy",
  "Manager / Reporting Issue",
  "Discrimination",
  "IT / Systems",
  "Facilities",
  "Ethics / Fraud",
  "Other",
];
const PRIORITIES = ["LOW", "MEDIUM", "HIGH", "CRITICAL"];

const statusConfig = {
  OPEN: { label: "Open", cls: "status-pending", icon: "bi-envelope-open" },
  IN_REVIEW: {
    label: "In Review",
    cls: "status-inreview",
    icon: "bi-search",
  },
  RESOLVED: {
    label: "Resolved",
    cls: "status-approved",
    icon: "bi-check-circle",
  },
  REJECTED: { label: "Rejected", cls: "status-rejected", icon: "bi-x-circle" },
};
const priorityConfig = {
  LOW: { cls: "status-hold", color: "#6b7280" },
  MEDIUM: { cls: "status-pending", color: "#b45309" },
  HIGH: { cls: "status-inreview", color: "#1d4ed8" },
  CRITICAL: { cls: "status-rejected", color: "#dc2626" },
};

const emptyForm = () => ({
  category: CATEGORIES[0],
  subject: "",
  description: "",
  priority: "MEDIUM",
  isAnonymous: false,
});

// Full lifecycle a complaint can move through — shown to the employee as a
// step tracker so they can see exactly where their complaint stands and what
// comes next, not just the current status pill. REJECTED is a terminal
// branch off IN_REVIEW rather than a 4th step in the normal path.
const LIFECYCLE_STEPS = [
  { key: "OPEN", label: "Filed", icon: "bi-envelope-open" },
  { key: "IN_REVIEW", label: "In Review", icon: "bi-search" },
  { key: "RESOLVED", label: "Resolved", icon: "bi-check-circle" },
];

// Renders a compact horizontal step tracker for a complaint's status. Steps
// before/at the current one are marked done/current; steps after are shown
// as pending (greyed out) so the whole journey is visible up front. If the
// complaint was rejected, the tracker shows the point it was rejected at.
function ComplaintStepper({ status }) {
  const isRejected = status === "REJECTED";
  const currentIdx = isRejected
    ? LIFECYCLE_STEPS.findIndex((s) => s.key === "IN_REVIEW")
    : LIFECYCLE_STEPS.findIndex((s) => s.key === status);

  return (
    <div className="d-flex align-items-center flex-wrap gap-0 mt-2 mb-1">
      {LIFECYCLE_STEPS.map((step, i) => {
        const done = i < currentIdx || (i === currentIdx && !isRejected);
        const isCurrent = i === currentIdx;
        const rejectedHere = isRejected && i === currentIdx;
        const color = rejectedHere
          ? "#dc2626"
          : done
            ? "#16a34a"
            : isCurrent
              ? "#1d4ed8"
              : "#d1d5db";
        return (
          <React.Fragment key={step.key}>
            <div
              className="d-flex align-items-center gap-1"
              style={{ opacity: i > currentIdx ? 0.5 : 1 }}
            >
              <span
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: done || isCurrent ? color : "#f3f4f6",
                  border: `1.5px solid ${color}`,
                }}
              >
                <i
                  className={`bi ${rejectedHere ? "bi-x-lg" : step.icon}`}
                  style={{
                    fontSize: "0.552rem",
                    color: done || isCurrent ? "#fff" : "#9ca3af",
                  }}
                />
              </span>
              <span
                style={{
                  fontSize: "0.7rem",
                  fontWeight: isCurrent ? 700 : 500,
                  color: isCurrent ? color : "#6b7280",
                }}
              >
                {rejectedHere ? "Rejected" : step.label}
              </span>
            </div>
            {i < LIFECYCLE_STEPS.length - 1 && (
              <div
                style={{
                  width: 20,
                  height: 2,
                  background: i < currentIdx ? "#16a34a" : "#e5e7eb",
                  margin: "0 4px",
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function ComplaintBox() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [attachFile, setAttachFile] = useState(null);
  const [uploadingAttach, setUploadingAttach] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("ALL");
  const [acting, setActing] = useState(null);
  const [viewFor, setViewFor] = useState(null);

  const load = () => {
    setLoading(true);
    complaintsAPI
      .list()
      .then((d) => setComplaints(d.complaints || []))
      .catch((e) => notify.error(e.message || "Failed to load complaints"))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
  }, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    if (!form.category || !form.subject || !form.description) {
      notify.error("Please fill category, subject and description");
      return;
    }
    setSaving(true);
    try {
      let attachment;
      if (attachFile) {
        setUploadingAttach(true);
        try {
          attachment = await uploadAttachment(attachFile, "complaints");
        } catch (upErr) {
          notify.warn(
            "Could not upload the attachment, submitting complaint without it.",
          );
        } finally {
          setUploadingAttach(false);
        }
      }
      await complaintsAPI.create({
        ...form,
        ...(attachment ? { attachment } : {}),
      });
      notify.success(
        "Complaint submitted. You can track its status here any time.",
      );
      setModal(false);
      setForm(emptyForm());
      setAttachFile(null);
      load();
    } catch (e) {
      notify.error(e.message || "Failed to submit complaint");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (c, nextStatus) => {
    const res = await confirm.complaintStatus(nextStatus, c.resolutionNote);
    if (!res.isConfirmed) return;
    setActing(c._id);
    try {
      await complaintsAPI.updateStatus(c._id, {
        status: nextStatus,
        remarks: res.value.remarks,
      });
      notify.success("Complaint status updated");
      load();
      setViewFor(null);
    } catch (e) {
      notify.error(e.message || "Failed to update status");
    } finally {
      setActing(null);
    }
  };

  const filtered =
    filter === "ALL"
      ? complaints
      : complaints.filter((c) => c.status === filter);
  const counts = {};
  ["OPEN", "IN_REVIEW", "RESOLVED", "REJECTED"].forEach((s) => {
    counts[s] = complaints.filter((c) => c.status === s).length;
  });

  const lc = useListControls(filtered, {
    searchFields: [
      (r) => r.complaintId,
      (r) => r.employeeName || r.raisedBy?.userName,
      "subject",
      "category",
    ],
    dateField: "createdAt",
    pageSize: 10,
  });

  return (
    <>
      <Header />
      <div id="db-wrapper">
        <main id="page-content">
          <Topbar section="Complaint Box" />
          <section className="container-fluid p-4 pt-3">
            <div className="page-hero d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div>
                <h5 className="text-white lh-1">Complaint Box</h5>
                <div className="sub">
                  {isAdmin
                    ? "Corporate complaint / grievance system — review and resolve every complaint filed"
                    : "Raise a workplace complaint confidentially and track its status"}
                </div>
              </div>
              <button
                onClick={() => {
                  setForm(emptyForm());
                  setAttachFile(null);
                  setModal(true);
                }}
                className="btn fw-semibold px-4 py-2"
                style={{
                  background: "rgba(255,255,255,0.2)",
                  color: "#fff",
                  borderRadius: 10,
                  border: "1.5px solid rgba(255,255,255,0.35)",
                }}
              >
                <i className="bi bi-plus-circle me-2" />
                File a Complaint
              </button>
            </div>

            <div className="row g-2 mb-3">
              {[
                {
                  label: "Open",
                  val: counts.OPEN || 0,
                  bg: "#fffbeb",
                  ic: "#f59e0b",
                  icon: "bi-envelope-open",
                },
                {
                  label: "In Review",
                  val: counts.IN_REVIEW || 0,
                  bg: "#eff6ff",
                  ic: "#3b82f6",
                  icon: "bi-search",
                },
                {
                  label: "Resolved",
                  val: counts.RESOLVED || 0,
                  bg: "#f0fdf4",
                  ic: "#22c55e",
                  icon: "bi-check-circle",
                },
                {
                  label: "Rejected",
                  val: counts.REJECTED || 0,
                  bg: "#fef2f2",
                  ic: "#ef4444",
                  icon: "bi-x-circle",
                },
              ].map((s, i) => (
                <div key={i} className="col-6 col-md-3">
                  <div className="kpi-card d-flex align-items-center gap-2">
                    <div className="kpi-icon" style={{ background: s.bg }}>
                      <i className={`bi ${s.icon}`} style={{ color: s.ic }} />
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: "1.17rem",
                          fontWeight: 700,
                          lineHeight: 1,
                        }}
                      >
                        {s.val}
                      </div>
                      <div
                        style={{
                          fontSize: "0.7rem",
                          color: "#6b7280",
                          marginTop: 2,
                        }}
                      >
                        {s.label}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="d-flex gap-2 mb-3 flex-wrap">
              {[
                ["ALL", "All", complaints.length],
                ["OPEN", "Open", counts.OPEN || 0],
                ["IN_REVIEW", "In Review", counts.IN_REVIEW || 0],
                ["RESOLVED", "Resolved", counts.RESOLVED || 0],
                ["REJECTED", "Rejected", counts.REJECTED || 0],
              ].map(([k, l, c]) => (
                <button
                  key={k}
                  className={`filter-chip ${filter === k ? "active" : "inactive"}`}
                  onClick={() => setFilter(k)}
                >
                  {l} <span className="chip-count">{c}</span>
                </button>
              ))}
            </div>

            <ListToolbar
              search={lc.search}
              setSearch={lc.setSearch}
              dateFrom={lc.dateFrom}
              setDateFrom={lc.setDateFrom}
              dateTo={lc.dateTo}
              setDateTo={lc.setDateTo}
              placeholder="Search by Complaint ID, subject or category..."
              dateLabel="Filed"
            />

            {!isAdmin && (
              <div
                className="mb-3"
                style={{
                  padding: "10px 14px",
                  background: "#eff6ff",
                  borderRadius: 10,
                  fontSize: "0.736rem",
                  color: "#1d4ed8",
                  border: "1px solid #bfdbfe",
                }}
              >
                <i className="bi bi-shield-lock me-1" />
                Only Admin can see complaints — you'll only ever see your own
                submissions and their status here.
              </div>
            )}

            {loading ? (
              <div className="text-center py-5">
                <span className="spinner-border text-primary" />
              </div>
            ) : lc.paginated.length === 0 ? (
              <div className="empty-state">
                <i className="bi bi-inbox" />
                <p>No complaints found</p>
              </div>
            ) : (
              lc.paginated.map((c, idx) => {
                const sc = statusConfig[c.status] || {
                  label: c.status,
                  cls: "status-pending",
                  icon: "bi-circle",
                };
                const pc = priorityConfig[c.priority] || priorityConfig.MEDIUM;
                return (
                  <div key={c._id} className="record-card">
                    <div className="record-number">
                      {(lc.page - 1) * lc.pageSize + idx + 1}.
                    </div>
                    <div className="record-body">
                      <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: "0.874rem",
                            color: "#111827",
                          }}
                        >
                          {c.subject}
                        </div>
                        <span className={`status-pill ${sc.cls}`}>
                          <i className={`bi ${sc.icon}`} />
                          {sc.label}
                        </span>
                        <span
                          className={`status-pill ${pc.cls}`}
                          style={{ color: pc.color }}
                        >
                          {c.priority}
                        </span>
                      </div>
                      <ComplaintStepper status={c.status} />
                      <div className="row g-2">
                        <div className="col-12 col-md-4">
                          <div className="record-field">
                            <span className="rf-label">Complaint ID — </span>
                            <span className="rf-value text-primary">
                              {c.complaintId}
                            </span>
                          </div>
                          <div className="record-field">
                            <span className="rf-label">Category — </span>
                            <span className="rf-value">{c.category}</span>
                          </div>
                        </div>
                        <div className="col-12 col-md-4">
                          {isAdmin && (
                            <div className="record-field">
                              <span className="rf-label">Filed By — </span>
                              <span className="rf-value">
                                {c.employeeName || c.raisedBy?.userName}
                                {c.department ? ` (${c.department})` : ""}
                              </span>
                            </div>
                          )}
                          <div className="record-field">
                            <span className="rf-label">Filed — </span>
                            <span className="rf-value">
                              {new Date(c.createdAt).toLocaleDateString(
                                "en-IN",
                              )}
                            </span>
                          </div>
                        </div>
                        <div className="col-12 col-md-4">
                          {c.resolvedAt && (
                            <div className="record-field">
                              <span className="rf-label">
                                {c.status === "RESOLVED"
                                  ? "Resolved — "
                                  : "Closed — "}
                              </span>
                              <span className="rf-value">
                                {new Date(c.resolvedAt).toLocaleDateString(
                                  "en-IN",
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mt-2">
                        <button
                          className="btn btn-outline-primary btn-sm rounded-3"
                          onClick={() => setViewFor(c)}
                        >
                          <i className="bi bi-eye me-1" />
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}

            <Pagination
              page={lc.page}
              totalPages={lc.totalPages}
              setPage={lc.setPage}
              totalCount={lc.totalCount}
              pageSize={lc.pageSize}
              itemLabel="complaints"
            />

            {/* File Complaint Modal */}
            {modal && (
              <div
                className="modal show d-block"
                style={{ background: "rgba(0,0,0,0.45)" }}
              >
                <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                  <div className="modal-content shadow-lg">
                    <div className="modal-header border-0">
                      <h5 className="fw-bold">
                        <i className="bi bi-megaphone me-2 text-danger" />
                        File a Complaint
                      </h5>
                      <button
                        className="btn-close"
                        onClick={() => setModal(false)}
                      />
                    </div>
                    <div className="modal-body">
                      {user && (
                        <div
                          style={{
                            padding: "10px 14px",
                            background: "#f0fdf4",
                            borderRadius: 10,
                            marginBottom: 8,
                            fontSize: "0.754rem",
                            color: "#16a34a",
                            border: "1px solid #bbf7d0",
                          }}
                        >
                          <b>{user.userName}</b> · {user.department}
                        </div>
                      )}
                      <div className="row g-2">
                        <div className="col-md-6">
                          <label className="form-label small fw-semibold">
                            Category *
                          </label>
                          <select
                            className="form-select"
                            value={form.category}
                            onChange={(e) => set("category", e.target.value)}
                          >
                            {CATEGORIES.map((c) => (
                              <option key={c}>{c}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label small fw-semibold">
                            Priority
                          </label>
                          <select
                            className="form-select"
                            value={form.priority}
                            onChange={(e) => set("priority", e.target.value)}
                          >
                            {PRIORITIES.map((p) => (
                              <option key={p}>{p}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-12">
                          <label className="form-label small fw-semibold">
                            Subject *
                          </label>
                          <input
                            className="form-control"
                            value={form.subject}
                            onChange={(e) => set("subject", e.target.value)}
                            placeholder="Brief summary of the issue"
                          />
                        </div>
                        <div className="col-12">
                          <label className="form-label small fw-semibold">
                            Reason / Description *
                          </label>
                          <textarea
                            className="form-control"
                            rows={5}
                            value={form.description}
                            onChange={(e) => set("description", e.target.value)}
                            placeholder="Describe what happened, when, and who was involved (if applicable)..."
                          />
                        </div>
                        <div className="col-12">
                          <label className="form-label small fw-semibold">
                            Attachment (optional)
                          </label>
                          <input
                            type="file"
                            className="form-control"
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                            onChange={(e) =>
                              setAttachFile(e.target.files?.[0] || null)
                            }
                          />
                          {attachFile && (
                            <div className="small text-success mt-1">
                              <i className="bi bi-paperclip me-1" />
                              {attachFile.name}
                            </div>
                          )}
                        </div>
                        <div className="col-12">
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="anonCheck"
                              checked={form.isAnonymous}
                              onChange={(e) =>
                                set("isAnonymous", e.target.checked)
                              }
                            />
                            <label
                              className="form-check-label small"
                              htmlFor="anonCheck"
                            >
                              Keep my identity hidden from anyone other than
                              Admin
                            </label>
                          </div>
                        </div>
                      </div>
                      <div
                        className="mt-3 p-3 rounded-3"
                        style={{
                          background: "#f8faff",
                          border: "1px solid #e0e7ff",
                          fontSize: "0.718rem",
                          color: "#374151",
                        }}
                      >
                        <i className="bi bi-shield-check me-2 text-primary" />
                        You'll be notified by email as the status changes.
                      </div>
                    </div>
                    <div className="modal-footer border-0 gap-2">
                      <button
                        className="btn btn-light rounded-3 px-4"
                        onClick={() => setModal(false)}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn rounded-3 px-5 fw-semibold"
                        style={{
                          background: "linear-gradient(135deg,#1a5276,#1e8449)",
                          color: "#fff",
                          border: "none",
                        }}
                        onClick={handleSubmit}
                        disabled={saving}
                      >
                        {saving ? (
                          <span className="spinner-border spinner-border-sm" />
                        ) : (
                          <>
                            <i className="bi bi-send me-1" />
                            {uploadingAttach
                              ? "Uploading..."
                              : "Submit Complaint"}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* View Details Modal */}
            {viewFor && (
              <div
                className="modal show d-block"
                style={{ background: "rgba(0,0,0,0.45)" }}
              >
                <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                  <div className="modal-content shadow-lg">
                    <div className="modal-header border-0">
                      <h5 className="fw-bold">
                        <i className="bi bi-file-earmark-text me-2 text-primary" />
                        {viewFor.complaintId}
                      </h5>
                      <button
                        className="btn-close"
                        onClick={() => setViewFor(null)}
                      />
                    </div>
                    <div className="modal-body">
                      <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                        <span
                          className={`status-pill ${(statusConfig[viewFor.status] || {}).cls || "status-pending"}`}
                        >
                          <i
                            className={`bi ${(statusConfig[viewFor.status] || {}).icon || "bi-circle"}`}
                          />
                          {(statusConfig[viewFor.status] || {}).label ||
                            viewFor.status}
                        </span>
                        <span
                          className={`status-pill ${(priorityConfig[viewFor.priority] || {}).cls}`}
                        >
                          {viewFor.priority} priority
                        </span>
                      </div>
                      <ComplaintStepper status={viewFor.status} />
                      <div className="row g-2 mb-3">
                        <div className="col-md-6">
                          <div className="record-field">
                            <span className="rf-label">Category — </span>
                            <span className="rf-value">{viewFor.category}</span>
                          </div>
                          {isAdmin && (
                            <div className="record-field">
                              <span className="rf-label">Filed By — </span>
                              <span className="rf-value">
                                {viewFor.employeeName ||
                                  viewFor.raisedBy?.userName}{" "}
                                ({viewFor.employeeCode || "—"})
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="col-md-6">
                          <div className="record-field">
                            <span className="rf-label">Filed — </span>
                            <span className="rf-value">
                              {new Date(viewFor.createdAt).toLocaleString(
                                "en-IN",
                              )}
                            </span>
                          </div>
                          <div className="record-field">
                            <span className="rf-label">Department — </span>
                            <span className="rf-value">
                              {viewFor.department || "—"}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: "0.782rem",
                          color: "#111827",
                          marginBottom: 6,
                        }}
                      >
                        {viewFor.subject}
                      </div>
                      <div
                        style={{
                          padding: "12px 14px",
                          background: "#f9fafb",
                          borderRadius: 10,
                          fontSize: "0.782rem",
                          color: "#374151",
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {viewFor.description}
                      </div>
                      {viewFor.attachment?.fileName && (
                        <div className="mt-2">
                          <a
                            href="#!"
                            onClick={(e) => {
                              e.preventDefault();
                              openExpenseAttachment(viewFor.attachment).catch(
                                () => notify.error("Could not open attachment"),
                              );
                            }}
                          >
                            <i className="bi bi-paperclip me-1" />
                            {viewFor.attachment.fileName}
                          </a>
                        </div>
                      )}

                      {viewFor.resolutionNote && (
                        <div
                          className="mt-3 p-3 rounded-3"
                          style={{
                            background:
                              viewFor.status === "REJECTED"
                                ? "#fef2f2"
                                : "#f0fdf4",
                            border: `1px solid ${viewFor.status === "REJECTED" ? "#fecaca" : "#bbf7d0"}`,
                            fontSize: "0.754rem",
                            color: "#374151",
                          }}
                        >
                          <b>
                            {viewFor.status === "REJECTED"
                              ? "Rejection note"
                              : "Resolution note"}
                          </b>
                          <div className="mt-1">{viewFor.resolutionNote}</div>
                        </div>
                      )}

                      {Array.isArray(viewFor.statusHistory) &&
                        viewFor.statusHistory.length > 0 && (
                          <div className="mt-3">
                            <div
                              style={{
                                fontWeight: 700,
                                fontSize: "0.736rem",
                                color: "#111827",
                                marginBottom: 6,
                              }}
                            >
                              Status History
                            </div>
                            {viewFor.statusHistory.map((h, i) => (
                              <div
                                key={i}
                                style={{
                                  fontSize: "0.718rem",
                                  color: "#6b7280",
                                  padding: "6px 0",
                                  borderBottom:
                                    i < viewFor.statusHistory.length - 1
                                      ? "1px solid #f3f4f6"
                                      : "none",
                                }}
                              >
                                <b style={{ color: "#111827" }}>
                                  {(statusConfig[h.status] || {}).label ||
                                    h.status}
                                </b>{" "}
                                {isAdmin && h.changedBy?.userName
                                  ? `by ${h.changedBy.userName}`
                                  : ""}{" "}
                                —{" "}
                                {new Date(h.changedAt).toLocaleString("en-IN")}
                                {h.remarks ? (
                                  <div style={{ marginTop: 2 }}>
                                    "{h.remarks}"
                                  </div>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                    <div className="modal-footer border-0 gap-2 flex-wrap">
                      <button
                        className="btn btn-light rounded-3 px-4"
                        onClick={() => setViewFor(null)}
                      >
                        Close
                      </button>
                      {isAdmin && viewFor.status === "OPEN" && (
                        <button
                          className="btn btn-outline-primary rounded-3 px-4"
                          onClick={() =>
                            handleStatusChange(viewFor, "IN_REVIEW")
                          }
                          disabled={acting === viewFor._id}
                        >
                          <i className="bi bi-search me-1" />
                          Mark In Review
                        </button>
                      )}
                      {isAdmin &&
                        ["OPEN", "IN_REVIEW"].includes(viewFor.status) && (
                          <>
                            <button
                              className="btn-card-approve"
                              onClick={() =>
                                handleStatusChange(viewFor, "RESOLVED")
                              }
                              disabled={acting === viewFor._id}
                            >
                              <i className="bi bi-check2-circle me-1" />
                              Resolve
                            </button>
                            <button
                              className="btn-card-reject"
                              onClick={() =>
                                handleStatusChange(viewFor, "REJECTED")
                              }
                              disabled={acting === viewFor._id}
                            >
                              <i className="bi bi-x-circle me-1" />
                              Reject
                            </button>
                          </>
                        )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>
        </main>
      </div>
    </>
  );
}
export default ComplaintBox;
